import { useEffect, useState } from 'react';
import {
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import toast from 'react-hot-toast';
import MuiDialog from '../../../UI/MuiDialog/MuiDialog';
import DragAndDrop from '../../../UI/DragAndDrop';
import useHttp from '../../../../hooks/use-http';
import { handleErrorMessage } from '../../../../../shared/api/handleErrorMessage';
import { urls } from '../api/urls';
import {
  formDataDela,
  procitajExcelDelova,
  SABLON_URL,
} from '../excel/uvozDelova';

const EXCEL_TIPOVI = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel': ['.xls'],
};
const ISTOVREMENIH_ZAHTEVA = 5;

const formatCene = (vrednost) =>
  vrednost === null || vrednost === undefined
    ? ''
    : Number(vrednost).toFixed(2);

const Status = ({ red }) => {
  if (red.uvezen) return <Chip size="small" color="success" label="Uvezeno" />;
  if (red.greskaUvoza) {
    return <span style={{ color: '#c62828' }}>Greška: {red.greskaUvoza}</span>;
  }
  if (red.greske.length) {
    return (
      <span style={{ color: '#c62828', whiteSpace: 'pre-line' }}>
        {red.greske.join('\n')}
      </span>
    );
  }
  return (
    <Chip size="small" color="primary" variant="outlined" label="Spremno" />
  );
};

const UvozExcelDialog = ({ open, onClose, masine, kategorije, onUvezeno }) => {
  const { getData } = useHttp('');
  const [fajlovi, setFajlovi] = useState([]);
  const [redovi, setRedovi] = useState([]);
  const [citanje, setCitanje] = useState(false);
  const [uvoz, setUvoz] = useState(false);
  const [obradjeno, setObradjeno] = useState(0);
  const [zavrseno, setZavrseno] = useState(false);

  useEffect(() => {
    if (!open) {
      setFajlovi([]);
      setRedovi([]);
      setObradjeno(0);
      setZavrseno(false);
    }
  }, [open]);

  const ispravni = redovi.filter((red) => !red.greske.length);
  const saGreskom = redovi.length - ispravni.length;

  const handleFajl = async (izabrani) => {
    setFajlovi(izabrani);
    setRedovi([]);
    setZavrseno(false);
    if (!izabrani.length) return;
    setCitanje(true);
    try {
      const postojeciDelovi = await getData({
        method: 'POST',
        url: urls.VARATI_DELOVE,
        data: { uslovPretrage: '' },
      });
      const procitani = await procitajExcelDelova(izabrani[0], {
        masine: Array.isArray(masine) ? masine : [],
        kategorije: Array.isArray(kategorije) ? kategorije : [],
        postojeciDelovi: Array.isArray(postojeciDelovi) ? postojeciDelovi : [],
      });
      if (!procitani.length) toast.error('U fajlu nema popunjenih redova.');
      setRedovi(procitani);
    } catch (greska) {
      toast.error(greska.message || 'Greška pri čitanju Excel fajla.');
    } finally {
      setCitanje(false);
    }
  };

  const uveziRed = async (red) => {
    const rezultat = await getData({
      method: 'POST',
      url: urls.INSERT_DELOVI,
      data: formDataDela(red.deo),
    });
    return rezultat?.id > 0
      ? { ...red, uvezen: true }
      : { ...red, greskaUvoza: handleErrorMessage(rezultat) };
  };

  const handleUvoz = async () => {
    if (!ispravni.length) {
      toast.error('Nema ispravnih redova za uvoz.');
      return;
    }
    setUvoz(true);
    setObradjeno(0);
    const rezultati = {};
    for (let i = 0; i < ispravni.length; i += ISTOVREMENIH_ZAHTEVA) {
      const grupa = ispravni.slice(i, i + ISTOVREMENIH_ZAHTEVA);
      // eslint-disable-next-line no-await-in-loop
      const uvezeni = await Promise.all(grupa.map(uveziRed));
      uvezeni.forEach((red) => {
        rezultati[red.brojReda] = red;
      });
      setObradjeno(Math.min(i + grupa.length, ispravni.length));
    }
    const novi = redovi.map((red) => rezultati[red.brojReda] || red);
    const brojUvezenih = novi.filter((red) => red.uvezen).length;
    setRedovi(novi);
    setUvoz(false);
    setZavrseno(true);
    if (brojUvezenih) onUvezeno();
    if (brojUvezenih === ispravni.length) {
      toast.success(`Uvezeno delova: ${brojUvezenih}.`);
    } else {
      toast.error(
        `Uvezeno ${brojUvezenih} od ${ispravni.length}. Pogledajte greške u tabeli.`,
      );
    }
  };

  return (
    <MuiDialog
      maxWidth="lg"
      open={open}
      title="Uvoz delova iz Excela"
      handleClose={uvoz ? () => {} : onClose}
      handleConfirm={zavrseno || !ispravni.length ? undefined : handleUvoz}
      loading={uvoz || citanje}
      content={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontSize: '14px', color: '#455a64' }}>
            Izaberite Excel fajl popunjen po šablonu (list „Delovi“, jedan red =
            jedan deo). Pre uvoza se prikazuje provera svakog reda. Uvoze se
            samo ispravni redovi. Slike se ne uvoze – dodaju se kasnije kroz
            izmenu dela.{' '}
            <a href={SABLON_URL} target="_blank" rel="noopener noreferrer">
              Preuzmi šablon
            </a>
          </div>
          <DragAndDrop
            handleFiles={handleFajl}
            accept={EXCEL_TIPOVI}
            maxFiles={1}
            addedFiles={fajlovi}
            handleRemoveFile={() => handleFajl([])}
            title="Excel fajl sa delovima"
          />
          {(citanje || uvoz) && (
            <LinearProgress
              variant={uvoz ? 'determinate' : 'indeterminate'}
              value={ispravni.length ? (obradjeno / ispravni.length) * 100 : 0}
            />
          )}
          {redovi.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Chip label={`Redova: ${redovi.length}`} />
                <Chip color="success" label={`Ispravnih: ${ispravni.length}`} />
                {saGreskom > 0 && (
                  <Chip color="error" label={`Sa greškom: ${saGreskom}`} />
                )}
                {uvoz && (
                  <Chip
                    color="warning"
                    label={`Uvoz: ${obradjeno} / ${ispravni.length}`}
                  />
                )}
              </div>
              <TableContainer
                style={{ maxHeight: '45vh', border: '1px solid #e0e0e0' }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        'Red',
                        'Naziv',
                        'Broj dela',
                        'Mašina',
                        'Kategorija',
                        'Nabavna',
                        'Marža %',
                        'Prodajna',
                        'Status',
                      ].map((naslov) => (
                        <TableCell key={naslov} style={{ fontWeight: 'bold' }}>
                          {naslov}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {redovi.map((red) => (
                      <TableRow
                        key={red.brojReda}
                        style={{
                          backgroundColor:
                            red.greske.length || red.greskaUvoza
                              ? '#fdecea'
                              : undefined,
                        }}
                      >
                        <TableCell>{red.brojReda}</TableCell>
                        <TableCell>{red.deo.naziv}</TableCell>
                        <TableCell>{red.deo.partNumber}</TableCell>
                        <TableCell>{red.deo.nazivMasine}</TableCell>
                        <TableCell>{red.deo.nazivKategorije}</TableCell>
                        <TableCell align="right">
                          {formatCene(red.deo.nabavnaCena)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCene(red.deo.marza)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCene(red.deo.prodajnaCena)}
                        </TableCell>
                        <TableCell>
                          <Status red={red} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </div>
      }
    />
  );
};

export default UvozExcelDialog;
